import { useEffect } from 'react';
import type { CatalogItem } from '@gorampe/catalog-adapter';

interface Props {
  item: CatalogItem | null;
  onClose: () => void;
}

interface PartLine {
  partSku: string;
  quantity: number;
  description?: string;
}

export function CatalogDetailModal({ item, onClose }: Props) {
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;
  const skuStr = item.sku as unknown as string;

  // Bundles have subProducts (SKU[]) ; products have parts (BomLine[])
  const rawAny = item.raw as unknown as {
    parts?: PartLine[];
    subProducts?: string[];
    descriptionEn?: string;
    usage?: string;
    taxable?: boolean;
    availableFloors?: string[];
    pricing?: { costCad: number; sellCad: number };
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="catalog-detail-title"
    >
      {/* Backdrop button — pure overlay click target, no visible content */}
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-transparent"
        onClick={onClose}
        aria-label="Fermer la fiche en cliquant à l'extérieur"
        tabIndex={-1}
      />
      <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 p-4">
          <div>
            <div className="font-mono text-sm text-slate-500">{skuStr}</div>
            <h2 id="catalog-detail-title" className="text-lg font-semibold text-slate-900">
              {item.label}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        <div className="grid gap-4 p-4 sm:grid-cols-2">
          <div className="aspect-square rounded bg-slate-50 flex items-center justify-center overflow-hidden">
            {item.imageUrl ? (
              <img src={item.imageUrl} alt={skuStr} className="h-full w-full object-contain" />
            ) : (
              <span className="text-sm text-slate-400">Pas d'image</span>
            )}
          </div>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-slate-500">Famille</dt>
              <dd className="font-medium text-slate-900">
                {item.family} — {item.familyLabel}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Catégorie</dt>
              <dd className="font-medium text-slate-900">
                {item.category}
                {item.subCategory ? ` / ${item.subCategory}` : ''}
              </dd>
            </div>
            {item.dimensionsLabel && (
              <div>
                <dt className="text-slate-500">Dimensions</dt>
                <dd className="font-medium text-slate-900">{item.dimensionsLabel}</dd>
              </div>
            )}
            {(item.thicknessIn != null || item.diameterIn != null) && (
              <div>
                <dt className="text-slate-500">
                  {item.diameterIn != null ? 'Diamètre' : 'Épaisseur'}
                </dt>
                <dd className="font-medium text-slate-900">
                  {item.diameterIn != null ? `Ø ${item.diameterIn}"` : `${item.thicknessIn}"`}
                </dd>
              </div>
            )}
            {item.weightLbs != null && (
              <div>
                <dt className="text-slate-500">Poids</dt>
                <dd className="font-medium text-slate-900">
                  {item.weightLbs.toFixed(2)} lbs
                  {item.weightKg != null && ` (${item.weightKg.toFixed(2)} kg)`}
                </dd>
              </div>
            )}
            {item.material && (
              <div>
                <dt className="text-slate-500">Matériau</dt>
                <dd className="font-medium text-slate-900 font-mono">{item.material}</dd>
              </div>
            )}
            <div>
              <dt className="text-slate-500">Prix vente CAD</dt>
              <dd className="text-xl font-bold text-slate-900 font-mono">
                {item.priceCad > 0 ? `${item.priceCad.toFixed(2)} $` : 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Type</dt>
              <dd className="font-medium text-slate-900">
                {item.kind === 'bundle' ? 'Ensemble (bundle)' : 'Pièce individuelle'}
              </dd>
            </div>
            {rawAny.descriptionEn && (
              <div>
                <dt className="text-slate-500">EN</dt>
                <dd className="text-slate-700">{rawAny.descriptionEn}</dd>
              </div>
            )}
            {rawAny.usage && (
              <div>
                <dt className="text-slate-500">Usage</dt>
                <dd className="text-slate-700">{rawAny.usage}</dd>
              </div>
            )}
            {(item.drawingDate || item.revision != null || item.scale || item.drawnBy) && (
              <div className="mt-3 border-t border-slate-100 pt-2">
                <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Dessin technique
                </dt>
                <dd className="mt-1 space-y-0.5 text-xs text-slate-700">
                  {item.drawingDate && <div>Date : {item.drawingDate}</div>}
                  {item.revision != null && <div>Révision : R{item.revision}</div>}
                  {item.scale && <div>Échelle : {item.scale}</div>}
                  {item.drawnBy && <div>Dessiné par : {item.drawnBy}</div>}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Product's parts (BOM) */}
        {item.kind === 'product' && rawAny.parts && rawAny.parts.length > 0 && (
          <div className="border-t border-slate-200 p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Composants ({rawAny.parts.length})
            </h3>
            <ul className="space-y-1 text-sm text-slate-600">
              {rawAny.parts.map((p, i) => (
                <li
                  key={`${p.partSku}-${i}`}
                  className="flex justify-between gap-3 border-b border-slate-100 py-1"
                >
                  <span className="min-w-0">
                    <span className="font-mono text-slate-700">{p.partSku}</span>
                    {p.description ? ` — ${p.description}` : ''}
                  </span>
                  <span className="font-medium tabular-nums text-slate-900 whitespace-nowrap">
                    ×{p.quantity}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Bundle's subProducts */}
        {item.kind === 'bundle' && rawAny.subProducts && rawAny.subProducts.length > 0 && (
          <div className="border-t border-slate-200 p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Sous-produits ({rawAny.subProducts.length})
            </h3>
            <ul className="space-y-1 text-sm text-slate-600">
              {rawAny.subProducts.map((sp, i) => (
                <li key={`${sp}-${i}`} className="font-mono border-b border-slate-100 py-1">
                  {sp}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
